import React, { useState, useEffect, useRef, useCallback } from 'react';

const FIGHTERS = { monkey: { emoji: '🐒', name: 'Changuito', speed: 3, power: 8 }, gorilla: { emoji: '🦍', name: 'Gorila', speed: 2, power: 12 } };

export default function MonkeyBoxing() {
  const canvasRef = useRef(null);
  const [started, setStarted] = useState(false);
  const [gameOver, setGameOver] = useState(false);
  const [winner, setWinner] = useState('');
  const [playerChar, setPlayerChar] = useState('monkey');
  const audioCtx = useRef(null);

  const gameRef = useRef({
    p1: { x: 80, hp: 100, action: 'idle', timer: 0 },
    p2: { x: 280, hp: 100, action: 'idle', timer: 0 },
    running: false,
  });

  const playSound = useCallback((type) => {
    try {
      if (!audioCtx.current) audioCtx.current = new (window.AudioContext || window.webkitAudioContext)();
      const ctx = audioCtx.current;
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain); gain.connect(ctx.destination); gain.gain.value = 0.08;
      if (type === 'punch') { osc.frequency.value = 150; osc.type = 'sawtooth'; gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.1); osc.start(); osc.stop(ctx.currentTime + 0.1); }
      else if (type === 'hit') { osc.frequency.value = 80; osc.type = 'square'; gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.15); osc.start(); osc.stop(ctx.currentTime + 0.15); }
      else if (type === 'ko') { osc.frequency.value = 300; osc.frequency.exponentialRampToValueAtTime(50, ctx.currentTime + 0.5); gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.6); osc.start(); osc.stop(ctx.currentTime + 0.6); }
    } catch {}
  }, []);

  const attack = useCallback((type) => {
    const g = gameRef.current;
    if (!g.running || g.p1.timer > 0) return;
    g.p1.action = type;
    g.p1.timer = type === 'block' ? 20 : 12;
    if (type !== 'block') playSound('punch');
  }, [playSound]);

  const startGame = useCallback(() => {
    const g = gameRef.current;
    g.p1 = { x: 80, hp: 100, action: 'idle', timer: 0 };
    g.p2 = { x: 280, hp: 100, action: 'idle', timer: 0 };
    g.running = true;
    setStarted(true); setGameOver(false); setWinner('');
  }, []);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    let animId;
    const p1f = FIGHTERS[playerChar];
    const p2f = playerChar === 'monkey' ? FIGHTERS.gorilla : FIGHTERS.monkey;

    const loop = () => {
      const g = gameRef.current;
      ctx.clearRect(0, 0, 400, 260);

      // Ring
      ctx.fillStyle = '#e8f0ea';
      ctx.fillRect(0, 0, 400, 260);
      ctx.fillStyle = '#d4e8d6';
      ctx.fillRect(20, 200, 360, 60);
      ctx.strokeStyle = '#2d6a4f';
      ctx.lineWidth = 3;
      ctx.strokeRect(20, 200, 360, 60);

      // HP bars
      ctx.fillStyle = '#ddd';
      ctx.fillRect(20, 15, 160, 14);
      ctx.fillRect(220, 15, 160, 14);
      ctx.fillStyle = g.p1.hp > 30 ? '#22c55e' : '#ef4444';
      ctx.fillRect(20, 15, Math.max(g.p1.hp * 1.6, 0), 14);
      ctx.fillStyle = g.p2.hp > 30 ? '#22c55e' : '#ef4444';
      ctx.fillRect(220, 15, Math.max(g.p2.hp * 1.6, 0), 14);

      // Names
      ctx.fillStyle = '#1a3a2a';
      ctx.font = 'bold 12px Arial';
      ctx.fillText(p1f.name, 20, 12);
      ctx.fillText(p2f.name, 320, 12);

      // Fighters
      const p1y = g.p1.action === 'punch' ? 155 : g.p1.action === 'uppercut' ? 145 : 160;
      const p2y = g.p2.action === 'punch' ? 155 : g.p2.action === 'uppercut' ? 145 : 160;
      ctx.font = g.p1.action === 'block' ? '36px serif' : '44px serif';
      ctx.fillText(p1f.emoji, g.p1.x, p1y);
      ctx.font = g.p2.action === 'block' ? '36px serif' : '44px serif';
      ctx.save();
      ctx.scale(-1, 1);
      ctx.fillText(p2f.emoji, -g.p2.x - 40, p2y);
      ctx.restore();

      // Action effects
      if (g.p1.action === 'punch' && g.p1.timer === 10) {
        ctx.font = '20px serif';
        ctx.fillText('💥', g.p1.x + 50, 155);
      }
      if (g.p1.action === 'uppercut' && g.p1.timer === 10) {
        ctx.font = '22px serif';
        ctx.fillText('⭐', g.p1.x + 50, 140);
      }
      if (g.p2.action === 'punch' && g.p2.timer === 10) {
        ctx.font = '20px serif';
        ctx.fillText('💥', g.p2.x - 30, 155);
      }

      if (!g.running) { animId = requestAnimationFrame(loop); return; }

      // Timers
      if (g.p1.timer > 0) g.p1.timer--;
      else g.p1.action = 'idle';
      if (g.p2.timer > 0) g.p2.timer--;
      else g.p2.action = 'idle';

      // P1 hit detection
      if (g.p1.action === 'punch' && g.p1.timer === 10) {
        if (g.p2.action === 'block') { playSound('hit'); }
        else { g.p2.hp -= p1f.power; playSound('hit'); }
      }
      if (g.p1.action === 'uppercut' && g.p1.timer === 10) {
        if (g.p2.action === 'block') { g.p2.hp -= Math.floor(p1f.power / 2); playSound('hit'); }
        else { g.p2.hp -= p1f.power * 1.5; playSound('hit'); }
      }

      // AI for P2
      if (g.p2.timer === 0) {
        const r = Math.random();
        if (g.p1.action === 'punch' || g.p1.action === 'uppercut') {
          if (r < 0.4) { g.p2.action = 'block'; g.p2.timer = 15; }
          else if (r < 0.6) { g.p2.action = 'punch'; g.p2.timer = 12; playSound('punch'); }
        } else {
          if (r < 0.03) { g.p2.action = 'punch'; g.p2.timer = 12; playSound('punch'); }
          else if (r < 0.04) { g.p2.action = 'uppercut'; g.p2.timer = 14; playSound('punch'); }
        }
      }

      // P2 hit detection
      if (g.p2.action === 'punch' && g.p2.timer === 10) {
        if (g.p1.action === 'block') { playSound('hit'); }
        else { g.p1.hp -= p2f.power; playSound('hit'); }
      }
      if (g.p2.action === 'uppercut' && g.p2.timer === 10) {
        if (g.p1.action === 'block') { g.p1.hp -= Math.floor(p2f.power / 2); playSound('hit'); }
        else { g.p1.hp -= p2f.power * 1.5; playSound('hit'); }
      }

      // KO check
      if (g.p1.hp <= 0) { g.running = false; setGameOver(true); setWinner(p2f.name); playSound('ko'); }
      if (g.p2.hp <= 0) { g.running = false; setGameOver(true); setWinner(p1f.name); playSound('ko'); }

      animId = requestAnimationFrame(loop);
    };
    animId = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(animId);
  }, [playerChar, playSound]);

  const btnStyle = (color) => ({
    padding: '14px 0', flex: 1, border: 'none', borderRadius: 12, cursor: 'pointer',
    fontSize: 14, fontWeight: 700, color: '#fff', background: color, touchAction: 'none',
  });

  return (
    <div style={{ textAlign: 'center', paddingBottom: 80 }}>
      <h2 style={{ margin: '10px 0' }}>🥊 Monkey Boxing</h2>

      {!started && (
        <div style={{ marginBottom: 14 }}>
          <div style={{ fontSize: 13, color: '#6b8f71', marginBottom: 8 }}>Elige tu peleador:</div>
          <div style={{ display: 'flex', gap: 10, justifyContent: 'center', marginBottom: 14 }}>
            {Object.entries(FIGHTERS).map(([k, f]) => (
              <button key={k} onClick={() => setPlayerChar(k)} style={{
                padding: '14px 20px', borderRadius: 14, cursor: 'pointer', fontSize: 15, fontWeight: 700,
                border: playerChar === k ? '3px solid #1a3a2a' : '1px solid #d4e8d6',
                background: playerChar === k ? '#e0f2e5' : '#fff', color: '#1a3a2a',
              }}>
                <span style={{ fontSize: 28 }}>{f.emoji}</span><br />{f.name}
              </button>
            ))}
          </div>
        </div>
      )}

      <div style={{ position: 'relative', display: 'inline-block' }}>
        <canvas ref={canvasRef} width={400} height={260} style={{
          border: '2px solid #1a3a2a', borderRadius: 14, width: '100%', height: 'auto', touchAction: 'none',
        }} />
        {!started && (
          <div onClick={startGame} onTouchStart={(e) => { e.preventDefault(); startGame(); }} style={{
            position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
            background: 'rgba(26,58,42,0.85)', borderRadius: 14, color: '#fff', cursor: 'pointer',
          }}>
            <div style={{ fontSize: 40 }}>🥊</div>
            <div style={{ fontSize: 18, fontWeight: 700, marginTop: 8 }}>Toca para pelear</div>
          </div>
        )}
        {gameOver && (
          <div onClick={startGame} onTouchStart={(e) => { e.preventDefault(); startGame(); }} style={{
            position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
            background: 'rgba(26,58,42,0.9)', borderRadius: 14, color: '#fff', cursor: 'pointer',
          }}>
            <div style={{ fontSize: 22, fontWeight: 800 }}>KO!</div>
            <div style={{ fontSize: 16, marginTop: 6 }}>🏆 {winner} gana</div>
            <div style={{ fontSize: 13, marginTop: 10, color: '#22c55e' }}>Toca para revancha</div>
          </div>
        )}
      </div>

      {started && !gameOver && (
        <div style={{ display: 'flex', gap: 8, marginTop: 12, maxWidth: 400, margin: '12px auto 0' }}>
          <button onClick={() => attack('punch')} onTouchStart={(e) => { e.preventDefault(); attack('punch'); }} style={btnStyle('#2d6a4f')}>👊 Golpe</button>
          <button onClick={() => attack('uppercut')} onTouchStart={(e) => { e.preventDefault(); attack('uppercut'); }} style={btnStyle('#d97706')}>⬆️ Upper</button>
          <button onClick={() => attack('block')} onTouchStart={(e) => { e.preventDefault(); attack('block'); }} style={btnStyle('#6b8f71')}>🛡️ Bloqueo</button>
        </div>
      )}
    </div>
  );
}
